import { api, NotificationStatus } from '@wellesley/openapi'
import { handleBasicResponse } from '@wellesley/openapi/helpers'
import { toast } from '@wellesley/system-toast'
import { logger } from 'helpers/logger'
import {
  setNotificationsPending,
  setNotificationsList,
  setNotificationsError,
  markNotification,
  setNotificationsUnreadCount,
  setNotificationsTotalCount,
} from '@wellesley/host-remote/api/actions'
import type { StoreWithUser } from '@wellesley/user/slices/userSlice'
import { notificationsSlice } from './slice'

export const notificationsApi = api.enhanceEndpoints({
  endpoints: {
    getApiV1DevicesVapidPublicKey: {
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await handleBasicResponse(queryFulfilled)
        } catch (e) {
          logger.error(`Get Vapid Public Key error, ${e as string}`)
        }
      },
    },
    putApiV1Devices: {
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await handleBasicResponse(queryFulfilled)
        } catch (e) {
          logger.error(`Register device error, ${e as string}`)
        }
      },
    },
    deleteApiV1DevicesByDeviceId: {
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await handleBasicResponse(queryFulfilled)
        } catch (e) {
          logger.error(`Delete device error, ${e as string}`)
        }
      },
    },
    getApiV1Notifications: {
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        dispatch(setNotificationsPending(true))
        try {
          const response = await handleBasicResponse(queryFulfilled)
          const store = getState() as unknown as StoreWithUser
          const userId = store.user.data?.selectedUserId || ''

          response &&
            dispatch(
              setNotificationsList({
                userId,
                list: response.data,
              }),
            )
        } catch (e) {
          dispatch(setNotificationsError(e))
        } finally {
          dispatch(setNotificationsPending(false))
        }
      },
    },
    postApiV1NotificationsByIdMark: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await handleBasicResponse(queryFulfilled)
          dispatch(markNotification(args))
        } catch (e) {
          toast.error(`Mark notification error, ${e as string}`)
        }
      },
    },
    postApiV1NotificationsMarkAll: {
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
        dispatch(setNotificationsUnreadCount(0))
        dispatch(notificationsSlice.actions.readAll())
      },
    },
    getApiV1NotificationsCount: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const response = (await handleBasicResponse(
            queryFulfilled,
          )) as unknown as { data: number }

          const isTotal = !(args as { status: NotificationStatus }).status

          if (response) {
            dispatch(
              isTotal
                ? setNotificationsTotalCount(response.data)
                : setNotificationsUnreadCount(response.data),
            )
          }
        } catch (e) {
          toast.error(
            `Error getting unread notifications count', ${e as string}`,
          )
        }
      },
    },
  },
})

export const {
  useGetApiV1NotificationsQuery,
  usePostApiV1NotificationsByIdMarkMutation,
  usePostApiV1NotificationsMarkAllMutation,
  useGetApiV1NotificationsCountQuery,
  useLazyGetApiV1DevicesVapidPublicKeyQuery,
  usePutApiV1DevicesMutation,
  useDeleteApiV1DevicesByDeviceIdMutation,
} = notificationsApi
